import React, { useRef, useState } from "react";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import styled from "styled-components";
import { theme } from "styles/theme";

type ViewType = "cards" | "map" | "table";

interface DevicesViewSwitcherProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  onAddDeviceClick?: () => void;
  onRequestTemporaryAccessClick?: () => void;
  temporaryRequestLimitReached?: boolean;
}

const Container = styled.div`
  display: flex;
  align-items: center;

  /* Mobile: toggle and Add Device stacked full width with even spacing */
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    width: 100%;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  background-color: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.surface};
  color: ${(props) =>
    props.$active ? theme.colors.onPrimary : theme.colors.textSecondary};
  border: none;
  cursor: pointer;
  font-weight: ${(props) =>
    props.$active ? theme.fontWeights.bold : theme.fontWeights.normal};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background-color: ${(props) =>
      props.$active ? theme.colors.primaryDark : theme.colors.bgLight};
    color: ${(props) =>
      props.$active ? theme.colors.onPrimary : theme.colors.textPrimary};
  }

  /* Mobile: equal thirds, 44px touch height, labels kept on one line */
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1;
    justify-content: center;
    min-height: 44px;
    padding: 0.5rem 0.375rem;
    white-space: nowrap;
  }
`;

const AddDeviceActions = styled.div`
  display: flex;
  margin-left: 8px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-left: 0;
  }
`;

const AddDeviceButton = styled(ViewButton)<{ $hasMenu: boolean }>`
  background-color: ${theme.colors.tertiary};
  color: ${theme.colors.onTertiary};
  font-weight: ${theme.fontWeights.medium};
  margin-left: 0;
  border-radius: ${(props) => (props.$hasMenu ? "4px 0 0 4px" : "4px")};

  &:hover {
    background-color: ${theme.colors.tertiaryDark};
    color: ${theme.colors.onTertiary};
  }
`;

const AddDeviceMenuButton = styled.button`
  width: 2.5rem;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 0 4px 4px 0;
  background-color: ${theme.colors.tertiary};
  color: ${theme.colors.onTertiary};
  cursor: pointer;
  transition: background-color 0.2s;

  /* Stays pressed while the menu is open; focus ring comes from the global teal :focus-visible */
  &:hover,
  &[aria-expanded="true"] {
    background-color: ${theme.colors.tertiaryDark};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 3rem;
  }
`;

const DevicesViewSwitcher: React.FC<DevicesViewSwitcherProps> = ({
  currentView,
  onViewChange,
  onAddDeviceClick,
  onRequestTemporaryAccessClick,
  temporaryRequestLimitReached = false,
}) => {
  const addDeviceMenu = useRef<Menu>(null);
  const [addDeviceMenuOpen, setAddDeviceMenuOpen] = useState(false);
  const addDeviceMenuItems: MenuItem[] = onRequestTemporaryAccessClick
    ? [
        {
          label: temporaryRequestLimitReached
            ? "Temporary access limit reached (10)"
            : "Request temporary access",
          icon: "pi pi-clock",
          disabled: temporaryRequestLimitReached,
          command: temporaryRequestLimitReached
            ? undefined
            : onRequestTemporaryAccessClick,
        },
      ]
    : [];
  const hasAddDeviceMenu = addDeviceMenuItems.length > 0;

  return (
    <Container>
      <ViewToggle>
        <ViewButton
          type="button"
          $active={currentView === "cards"}
          onClick={() => onViewChange("cards")}
        >
          <span className="pi pi-th-large" /> Cards
        </ViewButton>
        <ViewButton
          type="button"
          $active={currentView === "map"}
          onClick={() => onViewChange("map")}
        >
          <span className="pi pi-map-marker" /> Map View
        </ViewButton>
        <ViewButton
          type="button"
          $active={currentView === "table"}
          onClick={() => onViewChange("table")}
        >
          <span className="pi pi-table" /> List View
        </ViewButton>
      </ViewToggle>
      {onAddDeviceClick && (
        <AddDeviceActions>
          <AddDeviceButton
            type="button"
            $active
            $hasMenu={hasAddDeviceMenu}
            onClick={onAddDeviceClick}
          >
            <span className="pi pi-plus" /> Add Device
          </AddDeviceButton>
          {hasAddDeviceMenu && (
            <>
              <AddDeviceMenuButton
                type="button"
                aria-label="More add device options"
                aria-haspopup="menu"
                aria-expanded={addDeviceMenuOpen}
                aria-controls={
                  addDeviceMenuOpen ? "add-device-options-menu" : undefined
                }
                onClick={(event) => addDeviceMenu.current?.toggle(event)}
              >
                <span className="pi pi-chevron-down" />
              </AddDeviceMenuButton>
              <Menu
                id="add-device-options-menu"
                model={addDeviceMenuItems}
                popup
                popupAlignment="right"
                ref={addDeviceMenu}
                className="add-device-options-menu"
                onShow={() => setAddDeviceMenuOpen(true)}
                onHide={() => setAddDeviceMenuOpen(false)}
              />
            </>
          )}
        </AddDeviceActions>
      )}
    </Container>
  );
};

export default DevicesViewSwitcher;
